import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  HostListener
} from '@angular/core';
import { DataTablesConfigService } from '../../../services/datatables-config.service';
import {
  EbDataTableColumn,
  EbDataTableAction,
  EbDataTableActionEvent
} from './eb-datatable.models';

@Component({
  selector: 'app-eb-datatable',
  templateUrl: './eb-datatable.component.html',
  styleUrls: ['./eb-datatable.component.scss']
})
export class EbDataTableComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** DOM id (e.g. for E2E or legacy CSS). */
  @Input() tableId = 'ebDataTable';

  @Input() columns: EbDataTableColumn[] = [];

  /** Row objects: keys must match `columns[].key`. Can start empty; updates re-init the grid. */
  @Input() rows: any[] = [];

  /** Optional action buttons column; parent handles API via (actionClick). */
  @Input() actions: EbDataTableAction[] = [];

  /** Property on each row used to match button clicks (e.g. `id`). */
  @Input() rowIdKey = 'id';

  @Input() actionsHeader = 'Actions';

  @Input() pageLength = 10;

  @Input() orderColumnIndex = 0;

  @Input() orderDirection: 'asc' | 'desc' = 'asc';

  @Output() actionClick = new EventEmitter<EbDataTableActionEvent>();

  @ViewChild('tableEl', { static: false }) tableRef: ElementRef;

  private dt: any = null;
  private initRetry = 0;
  private readonly maxRetries = 15;
  private redrawTimer: any = null;
  private viewReady = false;

  constructor(private dataTablesConfigService: DataTablesConfigService) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scheduleRedraw();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows'] || changes['columns'] || changes['actions']) {
      this.scheduleRedraw();
    }
  }

  ngOnDestroy(): void {
    if (this.redrawTimer) {
      clearTimeout(this.redrawTimer);
      this.redrawTimer = null;
    }
    this.destroyDataTable();
  }

  private scheduleRedraw(): void {
    if (!this.viewReady) {
      return;
    }
    if (this.redrawTimer) {
      clearTimeout(this.redrawTimer);
    }
    this.redrawTimer = setTimeout(() => {
      this.redrawTimer = null;
      const raf =
        typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame
          : (cb: () => void) => setTimeout(cb, 16);
      raf(() => this.redrawTable());
    }, 0);
  }

  private destroyDataTable(): void {
    if (!this.tableRef || !this.tableRef.nativeElement) {
      return;
    }
    const el = this.tableRef.nativeElement;
    const $ = (window as any).jQuery || (window as any).$;
    if ($ && $.fn && $.fn.DataTable && $.fn.DataTable.isDataTable(el)) {
      try {
        $(el).DataTable().destroy();
      } catch (e) {}
    }
    this.dt = null;
  }

  private redrawTable(): void {
    if (!this.viewReady || !this.tableRef || !this.tableRef.nativeElement) {
      return;
    }
    if (!this.columns || this.columns.length === 0) {
      this.destroyDataTable();
      return;
    }

    const $ = (window as any).jQuery || (window as any).$;
    if (!$ || !$.fn || !$.fn.DataTable) {
      this.initRetry++;
      if (this.initRetry <= this.maxRetries) {
        setTimeout(() => this.redrawTable(), 200);
      }
      return;
    }
    this.initRetry = 0;

    this.destroyDataTable();

    const renderHtml = EbDataTableComponent.renderHtmlCell;
    const dtColumns: any[] = [];
    for (let i = 0; i < this.columns.length; i++) {
      const col = this.columns[i];
      const c: any = {
        data: col.key,
        width: col.width,
        className: col.className != null ? col.className : 'align-middle',
        orderable: col.orderable !== false,
        searchable: col.searchable !== false
      };
      if (col.cellType === 'html') {
        c.render = renderHtml;
      }
      dtColumns.push(c);
    }

    if (this.actions && this.actions.length > 0) {
      const self = this;
      dtColumns.push({
        data: null,
        width: '120px',
        className: 'align-middle',
        orderable: false,
        searchable: false,
        render: function (data: any, type: string, row: any) {
          if (type === 'display' || type === 'filter') {
            return self.buildActionsHtml(row);
          }
          return '';
        }
      });
    }

    const rowData = this.rows && this.rows.length ? this.rows.slice() : [];

    const config = this.dataTablesConfigService.getDefaultConfig({
      destroy: true,
      responsive: false,
      pageLength: this.pageLength,
      autoWidth: false,
      scrollX: true,
      data: rowData,
      columns: dtColumns,
      order: [[this.orderColumnIndex, this.orderDirection]] as any,
      columnDefs:
        this.actions && this.actions.length
          ? [{ targets: dtColumns.length - 1, orderable: false, searchable: false }]
          : []
    });

    try {
      this.dt = $(this.tableRef.nativeElement).DataTable(config);
    } catch (e) {
      console.error('EbDataTable init failed:', e);
      this.dt = null;
    }
  }

  private buildActionsHtml(row: any): string {
    if (!this.actions || !this.actions.length) {
      return '';
    }
    const rawId = row != null && row[this.rowIdKey] != null ? row[this.rowIdKey] : '';
    const idEsc = this.escapeAttr(String(rawId));
    let html = '<div class="btn-group eb-dt-actions" role="group">';
    for (let i = 0; i < this.actions.length; i++) {
      const a = this.actions[i];
      const nameEsc = this.escapeAttr(a.name);
      const titleAttr = a.title ? ' title="' + this.escapeAttr(a.title) + '"' : '';
      const icon = a.iconClass ? '<i class="' + this.escapeAttr(a.iconClass) + '"></i>' : '';
      const btnClass = a.btnClass ? a.btnClass : 'btn-outline-secondary';
      html +=
        '<button type="button" class="btn btn-sm eb-dt-action-btn ' +
        btnClass +
        '"' +
        titleAttr +
        ' data-eb-dt-action="' +
        nameEsc +
        '" data-eb-dt-row-id="' +
        idEsc +
        '">' +
        icon +
        '</button>';
    }
    html += '</div>';
    return html;
  }

  private escapeAttr(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  @HostListener('click', ['$event'])
  onTableClick(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (!t || typeof t.closest !== 'function') {
      return;
    }
    if (!this.tableRef || !this.tableRef.nativeElement.contains(t)) {
      return;
    }
    const btn = t.closest('button.eb-dt-action-btn') as HTMLButtonElement | null;
    if (!btn) {
      return;
    }
    const action = btn.getAttribute('data-eb-dt-action');
    const sid = btn.getAttribute('data-eb-dt-row-id');
    if (!action) {
      return;
    }
    const row = this.findRowById(sid);
    if (!row) {
      return;
    }
    const idVal = row[this.rowIdKey];
    this.actionClick.emit({
      action,
      row,
      id: idVal != null ? idVal : null
    });
  }

  private findRowById(sid: string | null): any {
    if (sid == null || !this.rows || !this.rows.length) {
      return null;
    }
    const rows = this.rows;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r && String(r[this.rowIdKey]) === String(sid)) {
        return r;
      }
    }
    return null;
  }

  private static renderHtmlCell(data: any, type: string): any {
    if (type === 'display' || type === 'filter') {
      return data != null ? data : '';
    }
    const $ = (window as any).jQuery || (window as any).$;
    if ($ && typeof data === 'string') {
      try {
        return $('<div>').html(data).text();
      } catch (e) {
        return data;
      }
    }
    return data;
  }
}
